import { aiConversations, aiMessages } from '@markku/db'
import type { AiChartSpec, AiSqlStep, AiStep, AiUsage } from '@markku/db'
import { createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

export const aiChartSpecSchema = z.object({
  type: z.enum(['bar', 'line', 'area', 'donut']),
  title: z.string().min(1),
  xKey: z.string().min(1),
  series: z
    .array(z.object({ key: z.string().min(1), label: z.string() }))
    .min(1),
  data: z
    .array(z.record(z.union([z.string(), z.number(), z.null()])))
    .min(1)
    .max(500),
  unit: z.string().optional(),
}) satisfies z.ZodType<AiChartSpec>

const aiConversationSchema = createSelectSchema(aiConversations).omit({
  deletedAt: true,
})

// What the API returns for a message: the row minus the soft-delete stamp.
export const aiMessageSchema = createSelectSchema(aiMessages, {
  steps: z.custom<AiStep[]>(),
  usage: z.custom<AiUsage | null>(),
}).omit({ deletedAt: true })

export const aiConversationListItemSchema = aiConversationSchema.extend({
  // Present for admins, who see everyone's threads.
  user: z
    .object({ id: z.number(), firstName: z.string(), lastName: z.string() })
    .optional(),
})

export const aiConversationWithMessagesSchema = aiConversationSchema.extend({
  messages: z.array(aiMessageSchema),
})

export const sendAiMessageSchema = z.object({
  conversationId: z.number().optional(),
  content: z.string().trim().min(1).max(4000),
})

// Still being answered: the ERP polls and locks the composer while this holds.
export const isAiMessageInProgress = (message: {
  status: AiMessage['status']
}) => message.status === 'pending' || message.status === 'running'

export type AiMessage = z.infer<typeof aiMessageSchema>
export type AiConversationWithMessages = z.infer<
  typeof aiConversationWithMessagesSchema
>
export type { AiChartSpec, AiSqlStep, AiStep, AiUsage }
